import { cn } from "@/lib/utils";

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  const renderMarkdown = (text: string) => {
    // Split by code blocks first to preserve them
    const parts = text.split(/(`[^`]+`)/g);
    
    return parts.map((part, index) => {
      // Inline code
      if (part.startsWith('`') && part.endsWith('`')) {
        return (
          <code 
            key={index} 
            className="bg-muted/50 px-1.5 py-0.5 rounded text-xs font-mono"
          >
            {part.slice(1, -1)}
          </code>
        );
      }
      
      // Process bold and italic in remaining text
      return <span key={index}>{processTextFormatting(part)}</span>;
    });
  };

  const processTextFormatting = (text: string) => {
    // Handle bold (**text** or __text__)
    const boldRegex = /\*\*([^*]+)\*\*|__([^_]+)__/g;
    // Handle italic (*text* or _text_) - but not inside bold
    const italicRegex = /(?<!\*)\*([^*]+)\*(?!\*)|(?<!_)_([^_]+)_(?!_)/g;
    
    let result: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;
    
    // First pass: handle bold
    const textWithBold: (string | JSX.Element)[] = [];
    lastIndex = 0;
    const boldMatches = [...text.matchAll(boldRegex)];
    
    boldMatches.forEach((match, matchIndex) => {
      if (match.index !== undefined) {
        // Add text before match
        if (match.index > lastIndex) {
          textWithBold.push(text.slice(lastIndex, match.index));
        }
        // Add bold text
        textWithBold.push(
          <strong key={`bold-${matchIndex}`} className="font-semibold">
            {match[1] || match[2]}
          </strong>
        );
        lastIndex = match.index + match[0].length;
      }
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
      textWithBold.push(text.slice(lastIndex));
    }
    
    // Second pass: handle italic in remaining string parts
    result = textWithBold.flatMap((part, partIndex) => {
      if (typeof part !== 'string') return part;
      
      const italicParts: (string | JSX.Element)[] = [];
      let italicLastIndex = 0;
      const italicMatches = [...part.matchAll(italicRegex)];
      
      italicMatches.forEach((match, matchIndex) => {
        if (match.index !== undefined) {
          if (match.index > italicLastIndex) {
            italicParts.push(part.slice(italicLastIndex, match.index));
          }
          italicParts.push(
            <em key={`italic-${partIndex}-${matchIndex}`} className="italic">
              {match[1] || match[2]}
            </em>
          );
          italicLastIndex = match.index + match[0].length;
        }
      });
      
      if (italicLastIndex < part.length) {
        italicParts.push(part.slice(italicLastIndex));
      }
      
      return italicParts.length > 0 ? italicParts : [part];
    });
    
    return result.length > 0 ? result : text;
  };

  // Split content into paragraphs and handle lists
  const renderContent = () => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let currentList: string[] = [];
    let listType: 'ul' | 'ol' | null = null;

    const flushList = () => {
      if (currentList.length > 0 && listType) {
        const ListTag = listType;
        elements.push(
          <ListTag 
            key={`list-${elements.length}`} 
            className={cn(
              "my-2 space-y-1",
              listType === 'ul' ? "list-disc pl-4" : "list-decimal pl-4"
            )}
          >
            {currentList.map((item, i) => (
              <li key={i} className="text-sm">
                {renderMarkdown(item)}
              </li>
            ))}
          </ListTag>
        );
        currentList = [];
        listType = null;
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Unordered list item
      if (/^[-*•]\s+/.test(trimmedLine)) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        currentList.push(trimmedLine.replace(/^[-*•]\s+/, ''));
        return;
      }
      
      // Ordered list item
      if (/^\d+\.\s+/.test(trimmedLine)) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        currentList.push(trimmedLine.replace(/^\d+\.\s+/, ''));
        return;
      }
      
      // Regular line - flush any pending list
      flushList();
      
      // Empty line
      if (!trimmedLine) {
        if (elements.length > 0 && index < lines.length - 1) {
          elements.push(<div key={`space-${index}`} className="h-2" />);
        }
        return;
      }
      
      // Regular paragraph
      elements.push(
        <p key={`p-${index}`} className="text-sm">
          {renderMarkdown(trimmedLine)}
        </p>
      );
    });
    
    // Flush any remaining list
    flushList();
    
    return elements;
  };

  return (
    <div className={cn("space-y-1", className)}>
      {renderContent()}
    </div>
  );
}
